import { j as jsxRuntimeExports } from "./auth-service-tools177.js";
import { forwardRef, useState, useRef, useEffect } from "react";
import { useScrollAreaContext } from "./auth-service-tools543.js";
import { useResizeObserver } from "./auth-service-tools544.js";
import { composeEventHandlers } from "./auth-service-tools550.js";
import { ScrollbarProvider } from "./auth-service-tools549.js";
import { useMergedRef } from "./auth-service-tools278.js";
import { useCallbackRef } from "./auth-service-tools566.js";
import { useDebouncedCallback } from "./auth-service-tools552.js";
const Scrollbar = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    sizes,
    hasThumb,
    onThumbChange,
    onThumbPointerUp,
    onThumbPointerDown,
    onThumbPositionChange,
    onDragScroll,
    onWheelScroll,
    onResize,
    ...scrollbarProps
  } = props;
  const context = useScrollAreaContext();
  const [scrollbar, setScrollbar] = useState(null);
  const composeRefs = useMergedRef(forwardedRef, node => setScrollbar(node));
  const rectRef = useRef(null);
  const prevWebkitUserSelectRef = useRef("");
  const {
    viewport
  } = context;
  const maxScrollPos = sizes.content - sizes.viewport;
  const handleWheelScroll = useCallbackRef(onWheelScroll);
  const handleThumbPositionChange = useCallbackRef(onThumbPositionChange);
  const handleResize = useDebouncedCallback(onResize, 10);

  const handleDragScroll = event => {
    if (rectRef.current) {
      const x = event.clientX - rectRef.current.left;
      const y = event.clientY - rectRef.current.top;
      onDragScroll({
        x,
        y
      });
    }
  };

  useEffect(() => {
    const handleWheel = event => {
      const element = event.target;
      const isScrollbarWheel = scrollbar == null ? void 0 : scrollbar.contains(element);

      if (isScrollbarWheel) {
        handleWheelScroll(event, maxScrollPos);
      }
    };

    document.addEventListener("wheel", handleWheel, {
      passive: false
    });
    return () => document.removeEventListener("wheel", handleWheel, {
      passive: false
    });
  }, [viewport, scrollbar, maxScrollPos, handleWheelScroll]);
  useEffect(handleThumbPositionChange, [sizes, handleThumbPositionChange]);
  useResizeObserver(scrollbar, handleResize);
  useResizeObserver(context.content, handleResize);
  return /* @__PURE__ */jsxRuntimeExports.jsx(ScrollbarProvider, {
    value: {
      scrollbar,
      hasThumb,
      onThumbChange: useCallbackRef(onThumbChange),
      onThumbPointerUp: useCallbackRef(onThumbPointerUp),
      onThumbPositionChange: handleThumbPositionChange,
      onThumbPointerDown: useCallbackRef(onThumbPointerDown)
    },
    children: /* @__PURE__ */jsxRuntimeExports.jsx("div", { ...scrollbarProps,
      ref: composeRefs,
      "data-mantine-scrollbar": true,
      style: {
        position: "absolute",
        ...scrollbarProps.style
      },
      onPointerDown: composeEventHandlers(props.onPointerDown, event => {
        event.preventDefault();
        const mainPointer = 0;

        if (event.button === mainPointer) {
          const element = event.target;
          element.setPointerCapture(event.pointerId);
          rectRef.current = scrollbar.getBoundingClientRect();
          prevWebkitUserSelectRef.current = document.body.style.webkitUserSelect;
          document.body.style.webkitUserSelect = "none";
          handleDragScroll(event);
        }
      }),
      onPointerMove: composeEventHandlers(props.onPointerMove, handleDragScroll),
      onPointerUp: composeEventHandlers(props.onPointerUp, event => {
        event.preventDefault();
        const element = event.target;

        if (element.hasPointerCapture(event.pointerId)) {
          element.releasePointerCapture(event.pointerId);
        }

        document.body.style.webkitUserSelect = prevWebkitUserSelectRef.current;
        rectRef.current = null;
      })
    })
  });
});
export { Scrollbar };